import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  CreateQuoteDocumentTemplateReplySchema,
  GetQuoteDocumentTemplateReplySchema,
  ListQuoteDocumentTemplatesReplySchema,
  UpdateQuoteDocumentTemplateReplySchema,
  type CreateQuoteDocumentTemplateParams,
  type QuoteDocumentTemplateDetail,
  type QuoteDocumentTemplateSummary,
  type UpdateQuoteDocumentTemplateParams,
} from "@forgepoint/shared";

/** The API path backing the org-wide quote-document templates. */
const TEMPLATES_PATH = "quote-document-templates";

/** Message shown when the template list cannot be read. */
const LIST_ERROR = "Failed to load quote document templates";

/** Message shown when a save-as-template call fails without a server message. */
const CREATE_ERROR = "Failed to save the document as a template";

/** Message shown when a template edit fails without a server message. */
const UPDATE_ERROR = "Failed to save the template";

/** HTTP status the create route uses for a template-name collision. */
const NAME_TAKEN_STATUS = 409;

/** Result of a save-as-template call: the template, or why it was refused. */
export interface CreateQuoteDocumentTemplateResult {
  /** The created template on success, else null. */
  template: QuoteDocumentTemplateSummary | null;
  /** The server error message on failure, else null. */
  error: string | null;
  /** True when the refusal was a template-name collision. */
  nameTaken: boolean;
}

interface QuoteDocumentTemplatesStore {
  /** The org's non-archived templates, keyed by template id. */
  templates: Map<string, QuoteDocumentTemplateSummary>;
  /** True once the list has been fetched, so consumers fetch only once. */
  hasFetched: boolean;
  /** True while the template list request is in flight. */
  isLoadingList: boolean;
  /** The last list-request error, or null. */
  listError: string | null;
  /** True while a save-as-template request is in flight. */
  isLoadingCreate: boolean;
  /** Fetches the org's templates, replacing the stored map. */
  fetchTemplates: () => Promise<void>;
  /** Saves a quote's stored document as a new template. */
  createTemplate: (
    params: CreateQuoteDocumentTemplateParams,
  ) => Promise<CreateQuoteDocumentTemplateResult>;
  /** Reads one template with its document, for seeding the template editor. */
  fetchTemplate: (
    templateId: string,
  ) => Promise<QuoteDocumentTemplateDetail | null>;
  /**
   * Replaces a template's document and/or name, returning the error message on
   * failure.
   */
  updateTemplate: (
    templateId: string,
    params: UpdateQuoteDocumentTemplateParams,
  ) => Promise<string | null>;
}

/**
 * Zustand store for the organization's quote-document templates. Templates are
 * held in an id-keyed map so callers pass ids around, with request state kept
 * per operation (list, create) since neither route mutates a single template.
 * The store never holds the templates' HTML: switching a quote to a template is
 * a server-side swap through the document PUT, so only identity is needed here.
 */
export const useQuoteDocumentTemplatesStore =
  create<QuoteDocumentTemplatesStore>((set) => ({
    templates: new Map(),
    hasFetched: false,
    isLoadingList: false,
    listError: null,
    isLoadingCreate: false,

    fetchTemplates: async (): Promise<void> => {
      set({ isLoadingList: true, listError: null });

      try {
        const response = await fetchApi(TEMPLATES_PATH, "GET");
        if (!response.ok || !response.data) {
          set({
            isLoadingList: false,
            hasFetched: true,
            listError: response.error ?? LIST_ERROR,
          });
          return;
        }
        const parsed = ListQuoteDocumentTemplatesReplySchema.parse(
          response.data,
        );
        set({
          templates: new Map(
            parsed.templates.map((template) => [template.id, template]),
          ),
          isLoadingList: false,
          hasFetched: true,
          listError: null,
        });
      } catch (error) {
        captureException(error as Error);
        set({ isLoadingList: false, hasFetched: true, listError: LIST_ERROR });
      }
    },

    createTemplate: async (
      params: CreateQuoteDocumentTemplateParams,
    ): Promise<CreateQuoteDocumentTemplateResult> => {
      set({ isLoadingCreate: true });

      try {
        const response = await fetchApi(TEMPLATES_PATH, "POST", {
          body: params,
        });
        if (response.ok && response.data) {
          const parsed = CreateQuoteDocumentTemplateReplySchema.parse(
            response.data,
          );
          set((state) => {
            const templates = new Map(state.templates);
            templates.set(parsed.template.id, parsed.template);
            return { templates, isLoadingCreate: false };
          });
          return { template: parsed.template, error: null, nameTaken: false };
        }
        set({ isLoadingCreate: false });
        return {
          template: null,
          error: response.error ?? CREATE_ERROR,
          nameTaken: response.status === NAME_TAKEN_STATUS,
        };
      } catch (error) {
        captureException(error as Error);
        set({ isLoadingCreate: false });
        return { template: null, error: CREATE_ERROR, nameTaken: false };
      }
    },

    fetchTemplate: async (
      templateId: string,
    ): Promise<QuoteDocumentTemplateDetail | null> => {
      try {
        const response = await fetchApi(
          `${TEMPLATES_PATH}/${templateId}`,
          "GET",
        );
        if (!response.ok || !response.data) {
          return null;
        }
        return GetQuoteDocumentTemplateReplySchema.parse(response.data)
          .template;
      } catch (error) {
        captureException(error as Error);
        return null;
      }
    },

    updateTemplate: async (
      templateId: string,
      params: UpdateQuoteDocumentTemplateParams,
    ): Promise<string | null> => {
      try {
        const response = await fetchApi(
          `${TEMPLATES_PATH}/${templateId}`,
          "PUT",
          { body: params },
        );
        if (!response.ok || !response.data) {
          return response.error ?? UPDATE_ERROR;
        }
        const parsed = UpdateQuoteDocumentTemplateReplySchema.parse(
          response.data,
        );
        set((state) => {
          const templates = new Map(state.templates);
          templates.set(parsed.template.id, parsed.template);
          return { templates };
        });
        return null;
      } catch (error) {
        captureException(error as Error);
        return UPDATE_ERROR;
      }
    },
  }));

/**
 * Reads the org's templates in name order, fetching them on first use.
 * @returns The org's non-archived templates, empty until they load
 */
export function useQuoteDocumentTemplates(): QuoteDocumentTemplateSummary[] {
  const templates = useQuoteDocumentTemplatesStore((state) => state.templates);
  const hasFetched = useQuoteDocumentTemplatesStore(
    (state) => state.hasFetched,
  );
  const isLoadingList = useQuoteDocumentTemplatesStore(
    (state) => state.isLoadingList,
  );
  const fetchTemplates = useQuoteDocumentTemplatesStore(
    (state) => state.fetchTemplates,
  );

  useEffect(() => {
    if (hasFetched || isLoadingList) {
      return;
    }
    fetchTemplates();
  }, [fetchTemplates, hasFetched, isLoadingList]);

  return useMemo(
    () =>
      [...templates.values()].sort((left, right) =>
        left.name.localeCompare(right.name),
      ),
    [templates],
  );
}
